import React, { useEffect, useState, useCallback } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  required,
  useCreate,
  useRedirect,
  useNotify,
  useGetList,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import { LocationOn as ZoneIcon } from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';

const ZonesCreate = () => {
  // Estado para almacenar el parámetro preseleccionado
  const [preselectedPlantId, setPreselectedPlantId] = useState<string | null>(
    null
  );

  // Estado para el filtro jerárquico
  const [filterValues, setFilterValues] =
    useState<HierarchicalFilterSelections>({});

  // Callback memoizado para el cambio de filtro
  const handleFilterChange = useCallback(
    (selections: HierarchicalFilterSelections) => {
      setFilterValues(selections);
    },
    []
  );

  // Obtener parámetros de la URL usando window.location de forma segura
  useEffect(() => {
    // Para hash routing, necesitamos obtener los parámetros del hash
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      const queryString = hash.substring(queryIndex + 1);
      const urlParams = new URLSearchParams(queryString);
      const plantId = urlParams.get('plantId');
      setPreselectedPlantId(plantId);

      // Si hay una planta preseleccionada, establecerla en el filtro
      if (plantId) {
        setFilterValues({ plantId: parseInt(plantId) });
      }
    }
  }, []);

  // Obtener lista de plantas
  const { data: plants, isLoading: plantsLoading } = useGetList('plants', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'plantName', order: 'ASC' },
  });

  // Hooks para manejar la creación personalizada
  const [create] = useCreate();
  const redirect = useRedirect();
  const notify = useNotify();

  // Función personalizada para manejar el envío del formulario
  const handleSave = async (data: any) => {
    try {
      // Usar la planta seleccionada del filtro o la preseleccionada
      const plantId =
        filterValues.plantId ||
        (preselectedPlantId ? parseInt(preselectedPlantId) : undefined);

      if (!plantId) {
        notify('Por favor selecciona una planta antes de crear la zona', {
          type: 'error',
        });
        return;
      }

      const payload = {
        ...data,
        plantId,
      };

      console.log('Payload enviado:', payload);

      await create('zones', { data: payload });

      notify('Zona creada exitosamente', { type: 'success' });

      // Redirección personalizada
      const redirectTo = '/zones';

      redirect(redirectTo);
    } catch (error) {
      console.error('Error al crear zona:', error);
      notify('Error al crear la zona', { type: 'error' });
    }
  };

  return (
    <Create redirect={false}>
      <Box sx={{ padding: 3, maxWidth: 800, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display='flex' alignItems='center' mb={3}>
              <ZoneIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Crear Nueva Zona
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Agrega una nueva zona al sistema
                </Typography>
              </Box>
            </Box>

            {/* Filtro Jerárquico para seleccionar planta */}
            <Card
              sx={{
                p: 3,
                mb: 3,
                boxShadow: 2,
                backgroundColor: 'background.default',
              }}
            >
              <Typography variant='h6' gutterBottom color='primary'>
                Seleccionar Planta
              </Typography>
              <HierarchicalFilter
                levels={['plant']}
                onChange={handleFilterChange}
                initialValues={filterValues}
                size='medium'
              />

              {!filterValues.plantId && (
                <Alert severity='warning' sx={{ mt: 2 }}>
                  Debes seleccionar una planta antes de crear la zona
                </Alert>
              )}

              {filterValues.plantId && (
                <Alert severity='success' sx={{ mt: 2 }}>
                  ✓ Planta seleccionada: {filterValues.plantName}
                </Alert>
              )}
            </Card>

            <SimpleForm
              onSubmit={handleSave}
              sx={{
                '& .RaSimpleForm-toolbar': {
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 2,
                },
              }}
            >
              <Grid container spacing={3}>
                {/* Información Principal */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <ZoneIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Información de la Zona
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <TextInput
                        source='zoneName'
                        label='Nombre de la Zona'
                        validate={[required()]}
                        fullWidth
                        sx={{ mb: 2 }}
                        helperText='Nombre descriptivo para la zona'
                        disabled={!filterValues.plantId}
                      />

                      {!filterValues.plantId && (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ fontStyle: 'italic' }}
                        >
                          Selecciona una planta arriba para habilitar este campo
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </SimpleForm>
          </CardContent>
        </Card>
      </Box>
    </Create>
  );
};

export default ZonesCreate;
